import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import { TextField, InputAdornment, Typography, Tooltip, IconButton, Table, TableHead, TableBody, TableRow, TableCell, Switch, Paper } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import api from '../api'

const styles = theme => ({
	paper: {
		padding: 0,
		maxHeight: 296,
		overflow: 'auto'
	},
	textField: {
		marginLeft: 0,
		marginRight: 0,
	},
	tableHead: {
		padding: '4px 16px 4px 8px',
		backgroundColor: '#424242',
		color: theme.palette.common.white,
		//		padding: '4px 16px 4px 8px',
		position: "sticky",
		top: 0,
	},
	tableCell: {
		padding: '4px 16px 4px 8px',
	},
	bold: {
		fontWeight: 500,
		fontSize: '1.2em',
	}
})

class DealerLocation extends React.Component {
	/**
	 * Handles the default constructor
	 * @param {props} props - the inbound props
	 * @returns {void} - no return value
	 * @public
	 */
	constructor(props) {
		super(props)

		this.state = {
			value: '',
			dealerLocation: '',
			dealerLocationId: -1,
			opened: false,
			requireInboundProgram: true,
			dealerLocations: []
		}

		this.onKeyPress = this.onKeyPress.bind(this)
		this.onChange = this.onChange.bind(this)
		this.onSearchClick = this.onSearchClick.bind(this)
		this.onRowClick = this.onRowClick.bind(this)
		this.toggleRequireInboundProgram = this.toggleRequireInboundProgram.bind(this)
	}

	componentDidMount = () => {
		this.setState({ requireInboundProgram: this.props.requireInboundProgram })
	}

	/**
	 * returns the address in 'street, city, state, zip' format
	 * @param {object} address - the address object of the selected dealer location
	 * @returns {string} - no return value
	 * @public
	 */
	formatAddress(address) {
		return (address.address + ', ' + address.city + ', ' + address.state + ' ' + address.zip)
	}

	/**
	 * Handles the keyPress event when the input is updated
	 * @param {event} event - the calling event
	 * @returns {void} - no return value
	 * @public
	 */
	onKeyPress(event) {
		if (event.key === 'Enter') {
			this.onSearchClick()
		}

		if (this.props.onKeyPress) {
			this.props.onKeyPress(event)
		}
	}

	/**
	 * Handles the click change event when the input is updated
	 * @param {event} event - the calling event
	 * @returns {void} - no return value
	 * @public
	 */
	onChange(event) {
		this.setState({
			value: event.target.value,
			dealerLocationId: -1,
			dealerLocation: '',
		})

		if (this.props.onChange) {
			this.props.onChange(event, -1)
		}
	}

	/**
	 * Handles the click event on the search button
	 * @param {event} event - the calling event
	 * @returns {void} - no return value
	 * @public
	 */
	async onSearchClick(event) {
		const values = this.state.value.split(',')
		let accountNumber = ''
		let dealer = ''
		let address = ''
		let city = ''

		switch (values.length) {
			case 0:
				this.setState({ opened: false })
				return

			case 1:
				accountNumber = values[0]
				dealer = values[0]
				address = values[0]
				city = values[0]
				break

			case 2:
				accountNumber = values[0]
				dealer = values[0]
				address = values[1]
				city = values[1]
				break

			case 3:
				accountNumber = values[0]
				dealer = values[0]
				address = values[1]
				city = values[2]
				break

			default:
				accountNumber = values[0]
				dealer = values[1]
				address = values[2]
				city = values[3]
		}

		const dealerResponse = await api.service('dealerLocations').find({
			query: {
				$and: {
					isBillTo: { $eq: true },
					$or: {
						accountNumber: { $eq: accountNumber },
						name: { $iLike: '%' + dealer + '%' }
					},
				},
				$include: ['address'],
				$limit: 2000,
			}
		})

		const addressResponse = await api.service('addresses').find({
			query: {
				$and: {
					address: { $iLike: '%' + address + '%' },
					city: { $iLike: '%' + city + '%' },
				},
				$include: ['dealerLocations.address'],
				$limit: 2000,
			}
		})

		let billToLocations = []
		addressResponse.data.filter(address => address.dealerLocations.length > 0).forEach(item => {
			item.dealerLocations.forEach(dealer => {
				if (dealer.isBillTo) {
					billToLocations.push(dealer)
				}
			})
		})

		const reduced = dealerResponse.data.filter(dealerItem => !billToLocations.find(locationItem => dealerItem.id === locationItem.id))

		let dealerLocations = [...reduced, ...billToLocations]

		if (this.state.requireInboundProgram && dealerLocations) {
			const inboundProgramResponse = await api.service('inboundProgramNumbers').find({
				query: {
					isDisabled: { $eq: false },
					$limit: 2000,
				}
			})

			dealerLocations = [...reduced, ...billToLocations].filter(locationItem => inboundProgramResponse.data.find(programItem => programItem.billTo === locationItem.accountNumber))
		}

		this.setState({
			opened: true,
			dealerLocations: dealerLocations.sort((a, b) => { return a.name.localeCompare(b.name) })
		})
	}

	/**
	 * Toggles the state of requireInboundProgram
	 * @returns {void} - no return value
	 * @public
	 */
	toggleRequireInboundProgram(event) {
		this.setState({ requireInboundProgram: event.target.checked })
	}

	/**
	 * Handles the click event on a row in the popup table
	 * @param {event} event - the calling event
	 * @param {number} id - the id number of the selected dealer location
	 * @param {string} accountNumber - the accout number of the selected dealerlocation
	 * @param {string} name - the name of the selected dealer location
	 * @param {object} address - the address object of the selected dealer location
	 * @returns {void} - no return value
	 * @public
	 */
	onRowClick(event, id, accountNumber, name, address) {
		const dealerLocation = accountNumber + ', ' + name + ', ' + this.formatAddress(address)

		this.setState({
			opened: false,
			dealerLocationId: id,
			dealerLocation: dealerLocation,
			dealerLocations: []
		})

		event.target.value = dealerLocation

		if (this.props.onChange) {
			this.props.onChange(event, id)
		}
	}

	render() {
		const { classes, name, label, placeholder, onBlur, autoFocus, disabled, required, error, errorMessage } = this.props
		const { value, dealerLocationId, dealerLocation, opened, dealerLocations } = this.state

		return (
			<div>
				<TextField
					id={name}
					name={name}
					label={label}
					className={classes.textField}
					value={dealerLocationId !== -1 ? dealerLocation : value}
					placeholder={placeholder}
					helperText={errorMessage}
					autoFocus={autoFocus}
					required={required}
					disabled={disabled}
					error={error || errorMessage !== ''}
					onChange={this.onChange}
					onBlur={onBlur}
					onKeyPress={this.onKeyPress}
					margin='dense'
					fullWidth={true}
					InputProps={{
						endAdornment:
							<InputAdornment position="end">
								<Tooltip title="Search for Dealer Locations">
									<IconButton onClick={this.onSearchClick} >
										<SearchIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Limit to Inbound Program Accounts?">
									<Switch
										checked={this.state.requireInboundProgram}
										onChange={this.toggleRequireInboundProgram}
										color="primary"
									/>
								</Tooltip>
							</InputAdornment>
					}}
				/>
				{opened &&
					<Paper className={classes.paper} square>
						<Table padding="dense">
							<TableHead>
								<TableRow>
									<TableCell className={classes.tableHead}>Account Number</TableCell>
									<TableCell className={classes.tableHead}>Dealer</TableCell>
									<TableCell className={classes.tableHead}>Address</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{dealerLocations.length === 0 &&
									<TableRow hover key='0'>
										<TableCell colSpan={3} className={classes.tableCell}>
											<Typography color="secondary" align="center" className={classes.bold}>
												There are no dealers that match your search criteria
											</Typography>
										</TableCell>
									</TableRow>
								}
								{dealerLocations.map(row => {
									return (
										<TableRow hover key={row.id} onClick={event => this.onRowClick(event, row.id, row.accountNumber, row.name, row.address)}>
											<TableCell className={classes.tableCell}>{row.accountNumber}</TableCell>
											<TableCell className={classes.tableCell}>{row.name}</TableCell>
											<TableCell className={classes.tableCell}>{this.formatAddress(row.address)}</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</Paper>
				}
			</div>
		)
	}
}

DealerLocation.propTypes = {
	classes: PropTypes.object,
	name: PropTypes.string.isRequired,
	label: PropTypes.string,
	requireInboundProgram: PropTypes.bool.isRequired,
	value: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]).isRequired,
	placeholder: PropTypes.string,
	error: PropTypes.bool,
	errorMessage: PropTypes.string,
	autoFocus: PropTypes.bool,
	disabled: PropTypes.bool,
	required: PropTypes.bool,
	onChange: PropTypes.func,
	onBlur: PropTypes.func,
	onKeyPress: PropTypes.func,
}

DealerLocation.defaultProps = {
	value: '',
	label: '',
	requireInboundProgram: true,
	placeholder: '',
	errorMessage: '',
	autoFocus: false,
	disabled: false,
	required: false,
	error: false,
}

export default withStyles(styles)(DealerLocation)


// WEBPACK FOOTER //
// ./src/components/DealerLocation.js